import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Search,
  Edit,
  Eye,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  X,
  Save,
  Plus,
  Calendar,
  CreditCard,
  MapPin,
  Briefcase,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoriasSocio = [
  { value: 'residente_1', label: 'Residente/Concurrente 1' },
  { value: 'residente_2', label: 'Residente/Concurrente 2' },
  { value: 'residente_3', label: 'Residente/Concurrente 3' },
  { value: 'residente_4', label: 'Residente/Concurrente 4' },
  { value: 'residente_5', label: 'Residente/Concurrente 5' },
  { value: 'especialista', label: 'Especialista' },
  { value: 'vitalicio', label: 'Vitalicio' },
  { value: 'honorario', label: 'Honorario' },
  { value: 'suscriptor', label: 'Suscriptor' }
];

const subespecialidades = [
  'Oftalmología General', 'Baja Visión', 'Catarata', 'Córnea',
  'Defensa Salud Visual', 'Estrabismo', 'Glaucoma', 'Gremialismo',
  'Infectología', 'Neurooftalmología', 'Oculoplástica y vías lagrimales',
  'Órbita', 'Oftalmopediatría', 'Refracción', 'Refractiva',
  'Praxis Médica', 'Presbicia', 'Retina', 'Superficie Ocular', 'Uveítis', 'Otra'
];

const provincias = [
  'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba',
  'Corrientes', 'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja',
  'Mendoza', 'Misiones', 'Neuquén', 'Río Negro', 'Salta', 'San Juan',
  'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero',
  'Tierra del Fuego', 'Tucumán'
];

const opcionesPago = [
  { value: 'debito_automatico', label: 'Débito automático' },
  { value: 'pago_anual', label: 'Pago anual' },
  { value: 'pago_semestral', label: 'Pago semestral' },
  { value: 'transferencia', label: 'Transferencia bancaria' }
];

const GestionUsuarios = () => {
  const { getAuthHeaders } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [soloSocios, setSoloSocios] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [crearUsuarioOpen, setCrearUsuarioOpen] = useState(false);
  const [nuevoUsuario, setNuevoUsuario] = useState({
    email: '', password: '', nombre: '', apellido: '', role: 'socio'
  });
  
  // Paginación
  const [pagina, setPagina] = useState(1);
  const porPagina = 10;

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busqueda) params.append('busqueda', busqueda);
      if (soloSocios) params.append('solo_socios', 'true');
      
      const response = await axios.get(
        `${API_URL}/api/interno/usuarios?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setUsuarios(response.data.usuarios);
      setPagina(1);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [soloSocios]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsuarios();
    }, 300);
    return () => clearTimeout(timer);
  }, [busqueda]);

  const handleVerUsuario = async (usuario) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/interno/usuarios/${usuario.id}`,
        { headers: getAuthHeaders() }
      );
      setUsuarioSeleccionado(response.data);
      setDialogOpen(true);
      setEditMode(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos del usuario');
    }
  };

  const handleCrearUsuario = async () => {
    if (!nuevoUsuario.email || !nuevoUsuario.password || !nuevoUsuario.nombre || !nuevoUsuario.apellido) {
      toast.error('Complete todos los campos');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/interno/usuarios`,
        nuevoUsuario,
        { headers: getAuthHeaders() }
      );
      toast.success('Usuario creado exitosamente');
      setCrearUsuarioOpen(false);
      setNuevoUsuario({ email: '', password: '', nombre: '', apellido: '', role: 'socio' });
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/interno/usuarios/${userId}/toggle-activo`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Estado actualizado');
      fetchUsuarios();
      if (usuarioSeleccionado?.usuario?.id === userId) {
        handleVerUsuario({ id: userId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleCambiarRol = async (userId, nuevoRol) => {
    try {
      await axios.put(`${API_URL}/api/interno/usuarios/${userId}`,
        { role: nuevoRol },
        { headers: getAuthHeaders() }
      );
      toast.success('Rol actualizado');
      fetchUsuarios();
      if (usuarioSeleccionado?.usuario?.id === userId) {
        handleVerUsuario({ id: userId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  // Paginación
  const totalPaginas = Math.ceil(usuarios.length / porPagina);
  const usuariosPaginados = usuarios.slice((pagina - 1) * porPagina, pagina * porPagina);

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-AR');
  };

  const formatFechaHora = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="gestion-usuarios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Gestión de Usuarios</h1>
          <p className="text-gray-600">Administre usuarios y complete datos faltantes</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{usuarios.length} usuarios</Badge>
          <Button 
            onClick={() => setCrearUsuarioOpen(true)} 
            className="bg-[#2853B9] hover:bg-[#1E3876]"
            data-testid="btn-crear-usuario"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="cao-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, email, CUIT o matrícula..."
                className="pl-10"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="soloSocios"
                checked={soloSocios}
                onCheckedChange={setSoloSocios}
              />
              <Label htmlFor="soloSocios" className="text-sm cursor-pointer">
                Solo socios
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="cao-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2853B9]"></div>
            </div>
          ) : usuarios.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No se encontraron usuarios
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Socio</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>CUIT/CUIL</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usuariosPaginados.map((usuario) => (
                      <TableRow key={usuario.id}>
                        <TableCell className="font-medium">
                          {usuario.nombre} {usuario.apellido}
                        </TableCell>
                        <TableCell>{usuario.email}</TableCell>
                        <TableCell>
                          {usuario.es_socio ? (
                            <Badge className="bg-green-100 text-green-700">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Sí
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">
                              <UserX className="w-3 h-3 mr-1" />
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {usuario.estado_socio ? (
                            <Badge className={
                              usuario.estado_socio === 'aprobado' ? 'bg-green-100 text-green-700' :
                              usuario.estado_socio === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                              usuario.estado_socio === 'devuelto' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {usuario.estado_socio}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {usuario.datos_socio?.categoria_socio?.replace(/_/g, ' ') || '-'}
                        </TableCell>
                        <TableCell>
                          {usuario.datos_socio?.cuit_cuil || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleVerUsuario(usuario)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Paginación */}
              {totalPaginas > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-gray-500">
                    Mostrando {((pagina - 1) * porPagina) + 1} - {Math.min(pagina * porPagina, usuarios.length)} de {usuarios.length}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagina(p => Math.max(1, p - 1))}
                      disabled={pagina === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      {pagina} / {totalPaginas}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                      disabled={pagina === totalPaginas}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog de detalle completo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalle de Usuario
            </DialogTitle>
            <DialogDescription>
              {usuarioSeleccionado?.usuario?.email}
            </DialogDescription>
          </DialogHeader>

          {usuarioSeleccionado && (
            <Tabs defaultValue="datos" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="datos">Datos Básicos</TabsTrigger>
                <TabsTrigger value="profesional">Profesional</TabsTrigger>
                <TabsTrigger value="direccion">Dirección</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
              </TabsList>

              {/* Tab: Datos Básicos */}
              <TabsContent value="datos" className="space-y-6 mt-4">
                {/* Info del usuario */}
                <div>
                  <h3 className="font-semibold text-[#1E3876] mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Datos del Usuario
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <Label className="text-xs text-gray-500">Nombre</Label>
                      <p className="font-medium">{usuarioSeleccionado.usuario.nombre}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Apellido</Label>
                      <p className="font-medium">{usuarioSeleccionado.usuario.apellido}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <p className="font-medium">{usuarioSeleccionado.usuario.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Rol</Label>
                      <Select
                        value={usuarioSeleccionado.usuario.role}
                        onValueChange={(value) => handleCambiarRol(usuarioSeleccionado.usuario.id, value)}
                      >
                        <SelectTrigger className="w-[150px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="socio">Socio</SelectItem>
                          <SelectItem value="interno">Interno</SelectItem>
                          <SelectItem value="controller">Controller</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Estado</Label>
                      <div className="flex items-center gap-2">
                        <Badge 
                          className={usuarioSeleccionado.usuario.activo !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                        >
                          {usuarioSeleccionado.usuario.activo !== false ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActivo(usuarioSeleccionado.usuario.id)}
                          className="text-xs"
                        >
                          {usuarioSeleccionado.usuario.activo !== false ? 'Desactivar' : 'Activar'}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Fecha Registro</Label>
                      <p className="font-medium">{formatFechaHora(usuarioSeleccionado.usuario.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Info del socio */}
                {usuarioSeleccionado.socio ? (
                  <div>
                    <h3 className="font-semibold text-[#1E3876] mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Datos del Socio
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-blue-50 p-4 rounded-lg">
                      <div>
                        <Label className="text-xs text-gray-500">Estado Solicitud</Label>
                        <Badge className={
                          usuarioSeleccionado.socio.estado === 'aprobado' ? 'bg-green-100 text-green-700' :
                          usuarioSeleccionado.socio.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                          usuarioSeleccionado.socio.estado === 'devuelto' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {usuarioSeleccionado.socio.estado}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fecha Alta Socio</Label>
                        <p className="font-medium">{formatFecha(usuarioSeleccionado.socio.datos?.fecha_alta)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fecha Nacimiento</Label>
                        <p className="font-medium">{formatFecha(usuarioSeleccionado.socio.datos?.fecha_nacimiento)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Categoría de Socio</Label>
                        <p className="font-medium">
                          {categoriasSocio.find(c => c.value === usuarioSeleccionado.socio.datos?.categoria_socio)?.label || '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">CUIT/CUIL</Label>
                        <p className="font-medium">{usuarioSeleccionado.socio.datos?.cuit_cuil || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Opción de Pago</Label>
                        <p className="font-medium">
                          {opcionesPago.find(o => o.value === usuarioSeleccionado.socio.datos?.opcion_pago)?.label || '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fecha Solicitud</Label>
                        <p className="font-medium">{formatFechaHora(usuarioSeleccionado.socio.created_at)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Última Actualización</Label>
                        <p className="font-medium">{formatFechaHora(usuarioSeleccionado.socio.updated_at)}</p>
                      </div>
                    </div>

                    {/* Datos de residencia si aplica */}
                    {usuarioSeleccionado.socio.datos?.categoria_socio?.includes('residente') && (
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-2">Datos de Residencia</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-500">Inicio Residencia</Label>
                            <p className="font-medium">{formatFecha(usuarioSeleccionado.socio.datos?.inicio_residencia)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Fin Residencia</Label>
                            <p className="font-medium">{formatFecha(usuarioSeleccionado.socio.datos?.fin_residencia)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fecha título especialista */}
                    {usuarioSeleccionado.socio.datos?.fecha_titulo_especialista && (
                      <div className="mt-4 p-4 bg-green-50 rounded-lg">
                        <div>
                          <Label className="text-xs text-gray-500">Fecha Título Especialista</Label>
                          <p className="font-medium">{formatFecha(usuarioSeleccionado.socio.datos?.fecha_titulo_especialista)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-500">
                    Este usuario no tiene registro de socio
                  </div>
                )}
              </TabsContent>

              {/* Tab: Profesional */}
              <TabsContent value="profesional" className="space-y-6 mt-4">
                {usuarioSeleccionado.socio ? (
                  <>
                    <div>
                      <h3 className="font-semibold text-[#1E3876] mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Matrícula Profesional
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <Label className="text-xs text-gray-500">Matrícula Nacional</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.matricula_nacional || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Matrícula Provincial</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.matricula_provincial || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Provincia Matrícula</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.provincia_matricula || '-'}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-[#1E3876] mb-3">Subespecialidades</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <Label className="text-xs text-gray-500">Subespecialidad Principal</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.subespecialidad_principal || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Subespecialidad 2</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.subespecialidad_2 || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Subespecialidad 3</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.subespecialidad_3 || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Guía de Profesionales */}
                    {usuarioSeleccionado.guia && (
                      <div>
                        <h3 className="font-semibold text-[#1E3876] mb-3">Guía de Profesionales</h3>
                        <div className="bg-green-50 p-4 rounded-lg space-y-3">
                          <div className="flex items-center justify-between">
                            <span>Estado en Guía:</span>
                            <Badge className={
                              usuarioSeleccionado.guia.estado === 'aprobado' ? 'bg-green-100 text-green-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {usuarioSeleccionado.guia.estado}
                            </Badge>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Breve CV</Label>
                            <p className="text-sm bg-white p-2 rounded">{usuarioSeleccionado.guia.datos?.breve_cv || '-'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-gray-500">LinkedIn</Label>
                              <p className="font-medium">{usuarioSeleccionado.guia.datos?.linkedin || '-'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Instagram</Label>
                              <p className="font-medium">{usuarioSeleccionado.guia.datos?.instagram || '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-500">
                    Este usuario no tiene registro de socio
                  </div>
                )}
              </TabsContent>

              {/* Tab: Dirección */}
              <TabsContent value="direccion" className="space-y-6 mt-4">
                {usuarioSeleccionado.socio ? (
                  <>
                    <div>
                      <h3 className="font-semibold text-[#1E3876] mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Dirección de Correspondencia
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="col-span-2">
                          <Label className="text-xs text-gray-500">Dirección</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.direccion || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Código Postal</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.codigo_postal || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Localidad</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.localidad || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Provincia</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.provincia || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">País</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.pais || 'Argentina'}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-[#1E3876] mb-3">Contacto para Terceros</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <Label className="text-xs text-gray-500">Autorización Datos</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.autorizacion_datos || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Email Terceros</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.email_terceros || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Teléfono Terceros</Label>
                          <p className="font-medium">{usuarioSeleccionado.socio.datos?.telefono_terceros || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Dirección de consultorio (guía) */}
                    {usuarioSeleccionado.guia?.datos?.direccion_principal && (
                      <div>
                        <h3 className="font-semibold text-[#1E3876] mb-3">Consultorio (Guía Profesionales)</h3>
                        <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                          <div>
                            <Label className="text-xs text-gray-500">Establecimiento</Label>
                            <p className="font-medium">{usuarioSeleccionado.guia.datos.direccion_principal.nombre_establecimiento}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-gray-500">Dirección</Label>
                              <p className="font-medium">
                                {usuarioSeleccionado.guia.datos.direccion_principal.calle} {usuarioSeleccionado.guia.datos.direccion_principal.numero}
                                {usuarioSeleccionado.guia.datos.direccion_principal.piso_depto && `, ${usuarioSeleccionado.guia.datos.direccion_principal.piso_depto}`}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Localidad</Label>
                              <p className="font-medium">
                                {usuarioSeleccionado.guia.datos.direccion_principal.localidad}, {usuarioSeleccionado.guia.datos.direccion_principal.provincia}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Teléfono Pacientes</Label>
                              <p className="font-medium">{usuarioSeleccionado.guia.datos.direccion_principal.telefono_pacientes}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Email Pacientes</Label>
                              <p className="font-medium">{usuarioSeleccionado.guia.datos.direccion_principal.email_pacientes || '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-500">
                    Este usuario no tiene registro de socio
                  </div>
                )}
              </TabsContent>

              {/* Tab: Documentos */}
              <TabsContent value="documentos" className="space-y-6 mt-4">
                {usuarioSeleccionado.socio ? (
                  <div>
                    <h3 className="font-semibold text-[#1E3876] mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documentación Adjunta
                    </h3>
                    <div className="space-y-3">
                      {[
                        { key: 'documento_identidad', label: 'Documento de Identidad' },
                        { key: 'documento_matricula', label: 'Matrícula Profesional' },
                        { key: 'titulo_medico', label: 'Título de Médico' },
                        { key: 'titulo_especialista', label: 'Título de Especialista' },
                        { key: 'certificado_residencia', label: 'Certificado de Residencia' }
                      ].map((doc) => (
                        <div key={doc.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{doc.label}</span>
                          {usuarioSeleccionado.socio.datos?.[doc.key] ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">Adjunto</Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const data = usuarioSeleccionado.socio.datos[doc.key];
                                  const win = window.open();
                                  win.document.write(`
                                    <html>
                                      <head><title>${doc.label}</title></head>
                                      <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                        ${data.startsWith('data:application/pdf') 
                                          ? `<iframe src="${data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                          : `<img src="${data}" style="max-width:100%;max-height:100vh;object-fit:contain;" />`
                                        }
                                      </body>
                                    </html>
                                  `);
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Ver
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">No adjunto</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-500">
                    Este usuario no tiene registro de socio
                  </div>
                )}

                {/* Foto de perfil (guía) */}
                {usuarioSeleccionado.guia?.datos?.foto_perfil && (
                  <div>
                    <h3 className="font-semibold text-[#1E3876] mb-3">Foto de Perfil (Guía)</h3>
                    <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                      <img 
                        src={usuarioSeleccionado.guia.datos.foto_perfil} 
                        alt="Foto de perfil"
                        className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Usuario */}
      <Dialog open={crearUsuarioOpen} onOpenChange={setCrearUsuarioOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription>
              Ingrese los datos del nuevo usuario
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nuevo-nombre">Nombre</Label>
                <Input
                  id="nuevo-nombre"
                  value={nuevoUsuario.nombre}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, nombre: e.target.value})}
                  placeholder="Nombre"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nuevo-apellido">Apellido</Label>
                <Input
                  id="nuevo-apellido"
                  value={nuevoUsuario.apellido}
                  onChange={(e) => setNuevoUsuario({...nuevoUsuario, apellido: e.target.value})}
                  placeholder="Apellido"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nuevo-email">Email</Label>
              <Input
                id="nuevo-email"
                type="email"
                value={nuevoUsuario.email}
                onChange={(e) => setNuevoUsuario({...nuevoUsuario, email: e.target.value})}
                placeholder="email@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nuevo-password">Contraseña</Label>
              <Input
                id="nuevo-password"
                type="password"
                value={nuevoUsuario.password}
                onChange={(e) => setNuevoUsuario({...nuevoUsuario, password: e.target.value})}
                placeholder="Contraseña"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nuevo-role">Rol</Label>
              <Select
                value={nuevoUsuario.role}
                onValueChange={(value) => setNuevoUsuario({...nuevoUsuario, role: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="socio">Socio</SelectItem>
                  <SelectItem value="interno">Interno (Staff)</SelectItem>
                  <SelectItem value="controller">Controller</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearUsuarioOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleCrearUsuario} 
              disabled={saving}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
            >
              {saving ? 'Creando...' : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUsuarios;
